/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  MasterLayout,
  MainLayout,
  ToolbarLayout,
  ToolbarLayoutLeft,
  ToolbarLayoutRight,
} from 'common/imports/content-layout';
import DashboardContext, { DashboardFilters, GroupedRobots } from '../context/dashboard.context';
import DashboardBody from './childs/DashboardBody';
import {
  AgentLogStatus,
  OperationSystemSessionState,
  RealTimeLogViewModel,
  StepLogModel,
} from '../model/dashboard.model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardService as Service } from '../services/dashboard.service';
import DashboardFilterDropdown from './DashboardFilterDropDown';

const mergeStepLogs = (existingStepLogs: GroupedRobots[], newStepLogs: StepLogModel[]): GroupedRobots[] => {
  const existingEventsMap: Record<number, StepLogModel> = existingStepLogs.reduce((map, robot) => {
    robot.events.forEach((event) => {
      if (event.eventId !== null && event.eventId !== undefined) {
        map[event.eventId] = event;
      }
    });
    return map;
  }, {} as Record<number, StepLogModel>);

  newStepLogs.forEach((newEvent) => {
    if (newEvent.eventId !== null && newEvent.eventId !== undefined) {
      const existingEvent = existingEventsMap[newEvent.eventId];
      if (existingEvent) {
        Object.assign(existingEvent, newEvent);
      } else {
        const robotIndex = existingStepLogs.findIndex((robot) => robot.key === newEvent.department);
        if (robotIndex !== -1) {
          existingStepLogs[robotIndex].events.push(newEvent);
        } else {
          existingStepLogs.push({ key: newEvent.department, events: [newEvent] });
        }
      }
    } else {
      const robotIndex = existingStepLogs.findIndex((robot) => robot.key === newEvent.department);
      if (robotIndex !== -1) {
        existingStepLogs[robotIndex].events.push(newEvent);
      } else {
        existingStepLogs.push({ key: newEvent.department, events: [newEvent] });
      }
    }
  });

  return existingStepLogs;
};

const filterSteps = (filters: DashboardFilters, toFilter: GroupedRobots[]): GroupedRobots[] => {
  if (filters.status) {
    toFilter = toFilter.map((gr) => {
      return {
        key: gr.key,
        events: gr.events.filter((e) => e.status === filters.status),
      };
    });
  }

  if (filters.department) {
    toFilter = toFilter.map((gr) => {
      return {
        key: gr.key,
        events: gr.events.filter((e) => e.department === filters.department?.name),
      };
    });
  }

  toFilter = toFilter.filter((gr) => gr.events.length > 0);
  return [...toFilter];
};

export function Dashboard() {
  const [groupedRobots, setGroupedRobots] = useState<GroupedRobots[]>([]);
  const [stepLogIsLoading, setStepLogIsLoading] = useState<boolean>(false);
  const [dashboardFilters, setDashboardFilters] = useState<DashboardFilters>({});
  const [IsFiltered, setIsFiltered] = useState<boolean>(false);

  const getStepLog = useCallback(async () => {
    setStepLogIsLoading(true);
    try {
      const result = await Promise.all([Service.getStepLog(), Service.getUnscheduledSteps()]);
      const hasErrors = result.some((e) => e == null || e.hasErrors);
      if (hasErrors) return;

      const steps = result.flatMap((e) => e!.data);
      const groupedSteps = mergeStepLogs([], steps);
      groupedSteps.forEach((s) => {
        s.events.forEach((e) => {
          if (!e.realTimeLogViewModel) return;
          e.percentage = e.realTimeLogViewModel.progress;
        });
      });

      setGroupedRobots(groupedSteps);
    } finally {
      setStepLogIsLoading(false);
    }
  }, []);

  const UpdateSingleItemStatusOnList = useCallback((status: AgentLogStatus, eventId: number) => {
    setGroupedRobots((current) =>
      current.map((robot) => {
        const updatedEvents = robot.events.map((event: StepLogModel) => {
          if (event.eventId === eventId) {
            return { ...event, status: status };
          }
          return event;
        });
        return { ...robot, events: updatedEvents };
      }),
    );
  }, []);

  const UpdateMachineStatusOnList = useCallback((machineName: string, sessionState: OperationSystemSessionState) => {
    setGroupedRobots((current) =>
      current.map((robot) => {
        const updatedEvents = robot.events.map((event: StepLogModel) => {
          if (event.machineName === machineName) {
            return { ...event, operationSystemSessionState: sessionState };
          }
          return event;
        });
        return { ...robot, events: updatedEvents };
      }),
    );
  }, []);

  const UpdateMachineConnectionOnList = useCallback((machineName: string, isUp: boolean) => {
    setGroupedRobots((current) =>
      current.map((robot) => {
        const updatedEvents = robot.events.map((event: StepLogModel) => {
          if (event.machineName === machineName) {
            return { ...event, machineIsUp: isUp };
          }
          return event;
        });
        return { ...robot, events: updatedEvents };
      }),
    );
  }, []);

  const UpdateEventProgress = useCallback(
    (eventId: number, stepId: number | undefined, status: AgentLogStatus, realTimeLog?: RealTimeLogViewModel) => {
      setGroupedRobots((current) =>
        current.map((robot) => {
          const updatedEvents = robot.events.map((event: StepLogModel) => {
            if (event.eventId === eventId || event.stepId === stepId) {
              return {
                ...event,
                status: status,
                realTimeLogViewModel: realTimeLog ?? event.realTimeLogViewModel,
                percentage: realTimeLog?.progress ?? event.percentage,
              };
            }
            return event;
          });
          return { ...robot, events: updatedEvents };
        }),
      );
    },
    [],
  );

  useEffect(() => {
    getStepLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredGroupedRobots = useMemo(() => {
    return filterSteps(dashboardFilters, groupedRobots);
  }, [dashboardFilters, groupedRobots]);

  const setFilter = (filters: DashboardFilters) => {
    setDashboardFilters(filters);
    setIsFiltered(filters.department !== undefined || filters.status !== undefined);
  };

  return (
    <MasterLayout>
      <DashboardContext.Provider
        value={{
          getStepLog,
          stepLogIsLoading,
          filteredGroupedRobots,
          dashboardFilters,
          UpdateSingleItemStatusOnList,
          UpdateMachineStatusOnList,
          UpdateMachineConnectionOnList,
          UpdateEventProgress,
          setFilter,
          filtered: IsFiltered,
        }}
      >
        <ToolbarLayout>
          <ToolbarLayoutLeft />
          <ToolbarLayoutRight>
            <DashboardFilterDropdown />
          </ToolbarLayoutRight>
        </ToolbarLayout>
        <MainLayout>
          <DashboardBody />
        </MainLayout>
      </DashboardContext.Provider>
    </MasterLayout>
  );
}
